import { RequireAuth } from '@/components/require-auth'
import {
  AgencyMemberForm,
  AgencyMemberFormValues,
  agencyMemberSchema,
} from '@/components/agency-member'
import { ApiError } from '@/services/auth-service'
import {
  UserService,
  type GetAgencyProfileResponse,
} from '@/services/user-service'
import {
  authState,
  updateAuthUser,
  type UserProfile,
} from '@/stores/auth-store'
import { Button, Notice, useForm } from '@tempots/beatui'
import { attr, html, MapSignal, on, prop, Signal } from '@tempots/dom'
import { Validation } from '@tempots/std'
import { HTMLTitle, NavigationService, Query } from '@tempots/ui'
import { EphimeralService } from '@/utils/ephimeral'
import { Track } from '@/services/track'
import { AppLayout } from '@/components/app-layout'

function optional(value: string | null | undefined): string | undefined {
  return value && value.trim() !== '' ? value.trim() : undefined
}

export function AgencyOnboardingPage({
  agencyId,
}: {
  agencyId: Signal<string>
}) {
  return RequireAuth({}, () => {
    const currentAgencyId = agencyId.value
    const user = authState.value.user

    return Query<string, GetAgencyProfileResponse>({
      request: currentAgencyId,
      load: ({ request: agencyIdToLoad }) =>
        UserService.getAgencyProfile(agencyIdToLoad),
      success: ({ value: profileResponse }) => {
        // Check if user already has an agency profile
        if (profileResponse.value.recruiterProfile != null) {
          // User already has a profile, redirect to process page
          NavigationService.navigate(`/${currentAgencyId}/workspace`, {
            replace: true,
          })
          return html.div() // Return empty div while redirecting
        }

        const errorMessage = prop<string | null>(null)
        const isSubmitting = prop(false)

        // Get the effective profile to pre-populate the form
        const effectiveProfile = profileResponse.value.recruiterProfile ?? {
          name: user?.fullName ?? '',
          email: user?.email ?? '',
          phone: undefined,
          linkedIn: undefined,
        }

        const { controller, submit } = useForm<AgencyMemberFormValues>({
          schema: agencyMemberSchema,
          initialValue: {
            recruiterName: effectiveProfile.name,
            recruiterEmail: effectiveProfile.email,
            recruiterPhoneNumber: effectiveProfile.phone,
            recruiterLinkedIn: effectiveProfile.linkedIn,
          },
          onSubmit: async data => {
            try {
              isSubmitting.set(true)
              errorMessage.set(null)

              Track.click({
                section: 'main',
                element_type: 'button',
                click_action: 'submit',
                element_id: 'signup',
              })

              const userName = data.recruiterName.trim()
              const userPhone = optional(data.recruiterPhoneNumber)
              const userLinkedIn = optional(data.recruiterLinkedIn)
              const userProfile: UserProfile = {}

              if (userName) {
                userProfile.name = userName
              }

              if (userPhone) {
                userProfile.phone = userPhone
              }

              if (userLinkedIn) {
                userProfile.linkedIn = userLinkedIn
              }

              if (data.recruiterEmail) {
                userProfile.email = data.recruiterEmail
              }

              // Update the agency-specific profile
              const response = await UserService.updateAgencyProfile(
                currentAgencyId,
                userProfile
              )

              // Also update the full name if changed
              if (userName !== effectiveProfile.name) {
                await UserService.updateCurrentUser({
                  fullName: userName,
                })
              }

              // Update auth store
              updateAuthUser(response.user)

              // Navigate to billing when signup carried a paid-plan
              // intent, otherwise to the agency's main page
              const planIntent = EphimeralService.retrieve<{
                type: 'planIntent'
                plan?: string
              }>('planIntent')
              NavigationService.navigate(
                planIntent?.plan
                  ? `/${currentAgencyId}/billing?intent=${planIntent.plan}`
                  : `/${currentAgencyId}/workspace`,
                { replace: true }
              )

              return Validation.valid
            } catch (error) {
              if (error instanceof ApiError) {
                const message =
                  error.message ??
                  'Failed to save your profile. Please try again.'
                errorMessage.set(message)
                Track.apiError({
                  error_code: error.code ?? 'AGENCY_ONBOARDING_FAILED',
                  error_message: message,
                })
                return Validation.invalid({ message })
              }

              const message = 'An unexpected error occurred. Please try again.'
              errorMessage.set(message)
              Track.clientError({
                error_code: 'AGENCY_ONBOARDING_UNEXPECTED_ERROR',
                error_message: message,
              })
              return Validation.invalid({ message })
            } finally {
              isSubmitting.set(false)
            }
          },
        })

        return AppLayout({
          agencyId,
          content: html.div(
            HTMLTitle('Complete Your Profile - ResumeHeaders'),
            attr.class('p-4 sm:p-6 md:p-8 h-full overflow-auto'),
            html.div(
              attr.class('max-w-192 mx-auto'),
              html.h2(
                attr.class('text-lg font-semibold text-gray-900 mb-2'),
                'Complete Your Profile for This Agency'
              ),
              html.p(
                attr.class('text-sm text-gray-600 mb-6'),
                'Please provide your contact information for this agency. This information will be used in resumes generated for this agency.'
              ),

              // Show error message (reactive — a submit failure sets this
              // after the initial render)
              MapSignal(errorMessage, msg =>
                msg
                  ? Notice(
                      {
                        variant: 'danger',
                        tone: 'prominent',
                        title: 'Error',
                      },
                      attr.class('mb-4'),
                      msg
                    )
                  : html.span()
              ),

              html.form(
                on.submit(submit),
                attr.class('flex flex-col gap-2 ml-4 sm:ml-8 my-8 max-w-128'),
                AgencyMemberForm({
                  controller,
                  onFieldChange: (fieldName, previousValue, newValue) =>
                    Track.fieldInput({
                      section: 'main',
                      field_name: fieldName,
                      previous_value: previousValue,
                      new_value: newValue,
                    }),
                }),
                html.div(
                  attr.class('flex flex-row gap-4 mt-4'),
                  html.div(attr.class('w-40')),
                  html.div(
                    attr.class('flex-grow flex gap-4'),
                    Button(
                      {
                        variant: 'outline',
                        onClick: () => {
                          Track.click({
                            section: 'main',
                            element_type: 'button',
                            click_action: 'navigate',
                            element_id: 'signup',
                          })
                          // Navigate back or to agency selection
                          NavigationService.navigate(
                            `/${currentAgencyId}/workspace`
                          )
                        },
                        disabled: isSubmitting,
                      },
                      'Skip for Now'
                    ),
                    Button(
                      {
                        // type must be the Button OPTION, not a child
                        // attr.type('submit'): beatui's click handler stops
                        // the event, so the attr-only form never submits.
                        type: 'submit',
                        color: 'primary',
                        loading: isSubmitting,
                        disabled: isSubmitting,
                      },
                      attr.class('flex-1'),
                      'Save and Continue'
                    )
                  )
                )
              ),

              html.div(
                attr.class('mt-8 p-4 bg-blue-50 rounded-lg'),
                html.p(
                  attr.class('text-sm text-teal-800'),
                  html.strong('Note: '),
                  "You can update this information later from your profile page. This information is specific to this agency and won't affect your other agencies."
                )
              )
            )
          ),
        })
      },
    })
  })
}
